import { useEffect, useRef } from 'react';
import { useIsMobile } from './use-mobile';

export interface UseScrollIntoViewOnFocusOptions {
  /**
   * Delay in milliseconds before scrolling.
   * Allows time for keyboard animation to start.
   * @default 300
   */
  delay?: number;

  /**
   * Scroll behavior options. Only 'behavior' is used when Visual Viewport API is available.
   * Falls back to full scrollIntoView on older browsers.
   * @default { behavior: 'smooth', block: 'start' }
   */
  scrollOptions?: ScrollIntoViewOptions;

  /**
   * Whether to enable this behavior
   * @default true
   */
  enabled?: boolean;

  /**
   * Buffer space in pixels below the element.
   * Element will scroll if it's within this distance of the keyboard.
   * Useful for dropdowns, autocomplete, or error messages that need space.
   * @default 20
   */
  bufferPx?: number;

  /**
   * Buffer space in pixels above the element.
   * Prevents the element from being scrolled too close to the viewport top.
   * Takes precedence over the bottom buffer if both can't be satisfied.
   * @default 60
   */
  topBufferPx?: number;
}

/**
 * Hook to handle mobile keyboard obstruction by scrolling the element into view when focused.
 *
 * Uses the Visual Viewport API to detect when the mobile keyboard actually obstructs the
 * element before scrolling. Calculates the precise scroll amount to:
 * - Show the buffer zone below the element (if possible)
 * - Maintain minimum distance from viewport top (topBufferPx takes precedence)
 *
 * Only scrolls if the element (plus bottom buffer) extends below the visible viewport,
 * preventing unnecessary scroll jumps.
 *
 * This is particularly useful for form fields and inputs that might be obscured by the
 * on-screen keyboard on mobile devices.
 *
 * @example Basic usage
 * ```tsx
 * function MyComponent() {
 *   const ref = useScrollIntoViewOnFocus();
 *   return <input ref={ref} />;
 * }
 * ```
 *
 * @example With buffer for dropdowns
 * ```tsx
 * function MyComponent() {
 *   const ref = useScrollIntoViewOnFocus({
 *     bufferPx: 200,      // Extra space for dropdown menu below
 *     topBufferPx: 80,    // Keep element away from sticky header
 *     scrollOptions: { behavior: 'smooth' }
 *   });
 *   return <select ref={ref}>...</select>;
 * }
 * ```
 */
export function useScrollIntoViewOnFocus<
  T extends HTMLElement = HTMLDivElement,
>(options: UseScrollIntoViewOnFocusOptions = {}) {
  const {
    delay = 300,
    scrollOptions = { behavior: 'smooth', block: 'start' },
    enabled = true,
    bufferPx = 20,
    topBufferPx = 60,
  } = options;

  const ref = useRef<T>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile || !enabled) return;

    const handleFocusIn = () => {
      // Small delay to allow keyboard to start appearing
      setTimeout(() => {
        if (!ref.current) return;

        // Fallback: if Visual Viewport API not available, use scrollIntoView
        if (!window.visualViewport) {
          ref.current.scrollIntoView(scrollOptions);
          return;
        }

        const rect = ref.current.getBoundingClientRect();
        const visibleBottom = window.visualViewport.height;

        // Check if element is obstructed (bottom + buffer extends below visible area)
        const isObstructed = rect.bottom + bufferPx > visibleBottom;
        if (!isObstructed) return; // No scroll needed

        // Calculate scroll amount to show buffer zone while respecting top buffer
        const idealScrollAmount = rect.bottom + bufferPx - visibleBottom;
        // Don't scroll element closer than topBufferPx to viewport top (takes precedence)
        const maxScrollAmount = Math.max(0, rect.top - topBufferPx);
        const scrollAmount = Math.min(idealScrollAmount, maxScrollAmount);

        if (scrollAmount > 0) {
          window.scrollBy({
            top: scrollAmount,
            behavior: scrollOptions.behavior || 'smooth',
          });
        }
      }, delay);
    };

    const element = ref.current;
    if (!element) return;

    // Listen for any focus events within the element
    element.addEventListener('focusin', handleFocusIn);

    return () => {
      element.removeEventListener('focusin', handleFocusIn);
    };
  }, [isMobile, enabled, delay, scrollOptions, bufferPx, topBufferPx]);

  return ref;
}
